import type {
    SxProps,
    Theme
} from '@mui/material/styles';

const styles = {
    combinedFormContainer: (theme) => ({
        alignItems: 'center',
        backgroundColor: theme.colors.white,
        border: `2px solid ${theme.colors.doveGray}`,
        borderRadius: 26,
        boxSizing: 'border-box',
        display: 'flex',
        gap: 2,
        height: 56,
        justifyContent: 'space-between',
        maxWidth: 910,
        p: '6px',
        width: '100%'
    }),

    divider: (theme) => ({
        backgroundColor: theme.colors.doveGray,
        height: 44,
        width: 2
    }),

    formInput: (theme) => ({
        '& .MuiFormControl-root': {
            width: '100%'
        },

        '& .MuiFormLabel-asterisk': {
            display: 'none'
        },

        '& .MuiInputBase-input': {
            '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
                WebkitBoxShadow: `0 0 0 1000px ${theme.palette.common.white} inset`
            },

            color: theme.colors.tundora,
            fontSize: 18,
            pl: 0
        },

        '& .MuiInputBase-root': {
            '&::after': {
                display: 'none'
            },

            '&::before': {
                display: 'none'
            }
        },

        '& .MuiInputLabel-root .MuiTypography-root': {
            color: theme.colors.doveGray
        },

        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            mt: '6px !important',
            transform: 'translate(0, -1.5px) scale(0.75)',
            zIndex: 1
        },

        '& .MuiInputLabel-sizeMedium': {
            mt: -1
        },

        backgroundColor: theme.palette.common.white,
        boxSizing: 'border-box',
        height: 52,
        minWidth: '100%',
        overflow: 'hidden'
    }),

    formInputError: (theme) => ({
        '& .MuiInputLabel-root .MuiTypography-root': {
            color: theme.colors.persianRed
        }
    }),

    formSelect: (theme) => ({
        '& .MuiFormLabel-root': {
            color: `${theme.colors.doveGray} !important`,
            top: '-7px'
        },

        '& .MuiFormLabel-root.Mui-focused': {
            color: `${theme.colors.doveGray} !important`
        },

        '& .MuiInputLabel-shrink': {
            '& + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend': {
                '& > span': {
                    pr: 6
                },

                maxWidth: 'fit-content'
            },

            ml: '8px !important',
            mt: '-3px !important',
            top: 0
        },

        '& .MuiInputLabel-sizeMedium': {
            fontSize: '18px !important',
            mt: '-7px'
        },

        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.colors.plantation} !important`,
            borderRadius: '22px !important',
            borderWidth: '1px !important',
            pl: '16px'
        },

        '& .MuiOutlinedInput-root': {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: `${theme.colors.plantation} !important`
            },

            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: `${theme.colors.plantation} !important`
            },

            height: 44
        },

        '& .MuiSelect-icon': {
            fontSize: '28px !important',
            top: 'calc(50% - 14px)'
        },

        '& .MuiSelect-select': {
            alignItems: 'center',
            boxSizing: 'border-box',
            display: 'flex',
            fontSize: 16,
            height: 44,
            px: 3
        },

        mt: 2,
        width: '100%'
    }),

    formSelectList: (theme) => ({
        '& .MuiMenuItem-root': {
            backgroundColor: 'transparent',
            border: 'none',
            color: theme.palette.common.white,
            fontSize: 14,
            m: 0,
            px: '13px',
            py: '7px'
        }
    }),

    formSelectPaper: (theme) => ({
        backgroundColor: theme.colors.bigStoneTranslucent,
        borderRadius: '0.3rem'
    }),

    innerButtonSpan: {
        ml: 1
    },

    inputLeft: {
        minWidth: 200
    },

    inputMarginLeft: {
        ml: 2
    },

    inputRight: {
        minWidth: 250
    },

    locationDot: {
        width: 20
    },

    locationDotInput: {
        mr: '4px',
        mt: 0
    },

    locationInput: {
        '& .marker': {
            fontSize: 20,
            mr: 1,
            mt: 1
        },

        '& input': {
            scrollMarginTop: 50
        },

        flex: 5
    },

    mobileButtonContainer: {
        alignItems: 'center',
        justifyContent: 'space-between'
    },

    mobileFilterButton: (theme) => ({
        '&:hover, &:focus-visible': {
            backgroundColor: theme.colors.silver
        },

        backgroundColor: theme.colors.lightGray,
        color: theme.colors.steelGray,
        flex: 1,
        minWidth: 128,
        px: {
            sm: 3,
            xs: 2
        }
    }),

    mobileSearchButton: (theme) => ({
        backgroundColor: theme.colors.plantation,
        color: theme.colors.white,
        flex: 2,
        px: {
            sm: 3,
            xs: 2
        }
    }),

    modal: (theme) => ({
        backgroundColor: theme.colors.white,
        borderColor: theme.colors.doveGray,
        borderRadius: '25px',
        borderStyle: 'solid',
        borderWidth: '1px',
        maxWidth: 535,
        p: {
            md: 4,
            xs: 2
        },
        position: 'relative',
        width: '100%'
    }),

    modalClose: (theme) => ({
        color: theme.colors.doveGray,
        minWidth: 0,
        position: 'absolute',
        right: 16,
        top: 8
    }),

    option: {
        alignItems: 'center',
        display: 'flex',
        gap: 1
    },

    overlay: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        p: 2
    },

    roundButton: {
        '& span': {
            margin: 0
        },

        alignItems: 'center',
        borderRadius: '50%',
        display: 'flex',
        height: 44,
        justifyContent: 'center',
        minWidth: 44,
        p: 0,
        width: 44
    },

    roundFilterButton: (theme) => ({
        '&:hover, &:focus-visible': {
            backgroundColor: theme.colors.silver
        },

        backgroundColor: theme.colors.lightGray,
        color: theme.colors.steelGray,
        position: 'relative'
    }),

    roundFilterButtonCount: (theme) => ({
        alignItems: 'center',
        background: theme.colors.secondaryDark,
        borderRadius: '50%',
        color: theme.palette.common.white,
        display: 'flex',
        fontSize: 9,
        height: 14,
        justifyContent: 'center',
        position: 'absolute',
        right: '-1px',
        top: '-1px',
        width: 14
    }),

    roundSearchButton: (theme) => ({
        backgroundColor: theme.colors.plantation,
        color: theme.colors.white
    }),

    splitInput: (theme) => ({
        '& .MuiInputBase-input': {
            width: '100%'
        },

        '& .MuiInputLabel-shrink': {
            mt: '6px !important'
        },

        '& .MuiInputLabel-sizeMedium': {
            mt: '-5px'
        },

        alignItems: 'center',
        border: `2px solid ${theme.colors.doveGray}`,
        borderRadius: 26,
        display: 'flex',
        mb: 2,
        px: 3,
        width: '100%'
    })
} satisfies Record<string, SxProps<Theme>>;

export default styles;
